import { ReactNode } from 'react';
import { Navigate, generatePath, useParams } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { getUserClassPath } from 'approot/classes/classes.apollo';
import {
  PRIVATE_ASSIGNMENT,
  PRIVATE_MY_ASSIGNMENTS,
} from 'global/private-routes.constants';
import { FEATURE_FLAG_MARKBOOK } from 'global/constants';

/**
 * Where a teacher without the markbook belongs instead. Marking emails deep-link
 * to the assessment report, so the assignment page it replaced is the answer
 * whenever the URL names one; the roll-ups have no single assignment to fall
 * back to.
 *
 * The class comes from the URL, not from `getUserClassPath`. Following a marking
 * email through sign-in leaves the current-class var on the teacher's default
 * class, so reading it there would send them to another class's copy of an
 * assignment id — which resolves to nothing and bounces them again.
 */
const getFallbackPath = ({
  assignmentId,
  userClassId,
}: {
  assignmentId?: string;
  userClassId?: string;
}): string => {
  const path = assignmentId
    ? generatePath(PRIVATE_ASSIGNMENT, { assignmentId })
    : PRIVATE_MY_ASSIGNMENTS;

  // Only the class-prefixed routes carry the id; the bare ones fall back to
  // whichever class is current, as every other link in the app does.
  return userClassId ? `/${userClassId}${path}` : getUserClassPath(path);
};

/**
 * Sits outside the report components so nothing queries before the flag is
 * read. Routing on the flag rather than gating the route with it — `LazyRoute`
 * renders Not Found, and these URLs arrive in marking emails sent to teachers
 * the flag may not be on for yet.
 */
const MarkbookFlagGuard = ({ children }: { children: ReactNode }) => {
  const flags = useFlags();
  const { assignmentId, userClassId } = useParams<{
    assignmentId?: string;
    userClassId?: string;
  }>();

  // Same wait as `PrivateRoute`: an empty bag means LaunchDarkly has not
  // answered yet, and redirecting on that would bounce everyone.
  if (Object.keys(flags).length === 0) return null;

  if (!flags[FEATURE_FLAG_MARKBOOK]) {
    return (
      <Navigate replace to={getFallbackPath({ assignmentId, userClassId })} />
    );
  }

  return <>{children}</>;
};

export default MarkbookFlagGuard;
